import { Conditional, Stack } from "src/shared/components"
import { Chip, Typography } from "@mui/material"
import SearchIcon from "@mui/icons-material/Search"
import OutlinedInput from "src/shared/components/outlined-input"
import ControlPointIcon from "@mui/icons-material/ControlPoint"
import { useMemo, useState } from "react"
import { usePermission } from "src/shared/hooks/usePermission"
import { DataGridMui } from "src/shared/container/data-grid-mui"
import getAuditListColumns from "./getAuditListColumns"
import CreateAuditDialog from "../../components/CreateAuditDialog"
import { AuditDetailModal } from "../../components/AuditDetailModal"
import {
  FilterCondition,
  FilterOperator,
  LogicalOperator,
  SortDirection,
  useCreateAuditMutation,
  useGetAllAuditsQuery
} from "src/graphql/quark/quark.type"
import ROUTE_PATHS from "src/routes/route-path"
import { useNavigate } from "react-router-dom"
import { useTypedSelector } from "src/shared/store/store"
import { useTranslation } from "react-i18next"

const AuditList = () => {
  const { t } = useTranslation("system-additional")
  const [isCreateAuditDialogOpen, setIsCreateAuditDialogOpen] = useState(false)
  const [selectedAuditId, setSelectedAuditId] = useState<string>("")
  const { hasPermission } = usePermission()
  const navigate = useNavigate()
  const currentLocation = useTypedSelector((state) => state.authentication.currentWhLoc)
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 20 })
  const [filters, setFilters] = useState<{ id: string; value: string }[]>([])
  const [sorting, setSorting] = useState<{ id?: null | string; desc?: null | boolean }>({
    id: "secondaryId",
    desc: true
  })
  const filterConditions: FilterCondition[] = useMemo(() => {
    if (!filters?.length) return []
    return filters.map(
      (val) =>
        ({
          operator: val.id === "secondaryId" ? FilterOperator.Equal : FilterOperator.Like,
          field: val.id,
          value: val.value
        }) as FilterCondition
    )
  }, [filters])

  const {
    data: auditLisResp,
    loading: auditListLoading,
    refetch
  } = useGetAllAuditsQuery({
    fetchPolicy: "network-only",
    variables: {
      locationId: currentLocation?.id,
      limit: pagination.pageSize,
      page: pagination.pageIndex + 1,
      filter: {
        conditions: filterConditions,
        logicalOperator: LogicalOperator.And
      },
      sort: sorting?.id
        ? [
            {
              direction: sorting?.desc ? SortDirection.Desc : SortDirection.Asc,
              field: sorting.id
            }
          ]
        : []
    }
  })

  const columns = useMemo(
    () =>
      getAuditListColumns({
        onAction: (type, id) => {
          if (type === "detail") {
            setSelectedAuditId(id)
          } else if (type === "continue") {
            navigate(ROUTE_PATHS.AUDIT.EXECUTION(id))
            // TODO: Implement continue action
          }
        }
      }),
    []
  )
  const [createAudit, { loading: createAuditLoading }] = useCreateAuditMutation()

  const handleCloseAuditModal = () => {
    setSelectedAuditId("")
  }

  return (
    <>
      <Stack height={"100%"} p={2}>
        <Stack variant="rowCenterSpaceBetween" mb={2}>
          <Stack>
            <Typography variant="h3">{t("audit.title")}</Typography>
            <Typography variant="body2" color="text.secondary">
              {t("audit.subtitle")}
            </Typography>
          </Stack>
        </Stack>

        <DataGridMui
          id="audit-list-table"
          data={auditLisResp?.getAllAudits?.data ?? []}
          isLoading={auditListLoading}
          onRefresh={refetch}
          paginationModel={pagination}
          paginationMode="server"
          sortingMode="server"
          filterMode="server"
          rowsPerPageOptions={[20, 30, 50]}
          initialPageSize={20}
          skeletonCount={20}
          sortingModel={sorting as any}
          onSortModelChange={setSorting}
          enableColumnFilters
          onFilterChange={setFilters}
          onPaginationChange={setPagination}
          columns={columns as any}
          leftComponent={
            <>
              <Conditional condition={hasPermission("system:audit:read")}>
                <OutlinedInput
                  variant="rounded"
                  startAdornment={<SearchIcon />}
                  placeholder={t("audit.searchPlaceholder")}
                  size="small"
                />
              </Conditional>
            </>
          }
          rightComponent={
            <Stack my={2} spacing={2} direction={"row"} alignItems={"center"}>
              <Conditional condition={hasPermission("system:audit:create")}>
                <Chip
                  onClick={() => setIsCreateAuditDialogOpen(true)}
                  size="small"
                  color="primary"
                  clickable
                  variant="outlined"
                  icon={<ControlPointIcon fontSize="small" />}
                  label={t("audit.createButton")}
                />
              </Conditional>
            </Stack>
          }
        />
      </Stack>

      <CreateAuditDialog
        open={isCreateAuditDialogOpen}
        onClose={() => setIsCreateAuditDialogOpen(false)}
        onSubmit={async (e) => {
          const res = await createAudit({ variables: { createAuditDto: e } })
          if (res.data?.createAudit?.id) {
            refetch()
            return true
          }
          return false
        }}
        loading={createAuditLoading}
      />

      {/* Audit Detail Modal */}
      <AuditDetailModal open={!!selectedAuditId} onClose={handleCloseAuditModal} auditId={selectedAuditId} />
    </>
  )
}

export default AuditList
